import { BUXUS_GET_TRAINING_DOCUMENTS_URL } from '../platform/abp/config'
import { httpJson } from '../platform/abp/httpClient'
import type { TrainingDocumentDto } from './trainingDocument'

type AbpListEnvelope = {
  result?: unknown
  data?: unknown
}

function pickList(raw: unknown): unknown[] {
  if (Array.isArray(raw)) return raw
  if (!raw || typeof raw !== 'object') return []
  const wrapped = raw as AbpListEnvelope
  const inner = wrapped.result ?? wrapped.data
  return Array.isArray(inner) ? inner : []
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value : value == null ? '' : String(value)
}

function asNullableString(value: unknown): string | null {
  if (value == null) return null
  const s = String(value)
  return s.trim() ? s : null
}

function asNumber(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim()) {
    const n = Number(value)
    return Number.isFinite(n) ? n : 0
  }
  return 0
}

function normalizeTrainingDocument(raw: unknown): TrainingDocumentDto | null {
  if (!raw || typeof raw !== 'object') return null
  const row = raw as Record<string, unknown>
  const id = asString(row.id ?? row.Id).trim()
  const fileName = asString(row.fileName ?? row.FileName).trim()
  const sasUrl = asString(row.sasUrl ?? row.SasUrl).trim()
  if (!id && !fileName) return null
  if (!sasUrl) return null
  const title = asString(row.title ?? row.Title).trim() || fileName.replace(/\.[^.]+$/, '')
  return {
    id: id || fileName,
    fileName: fileName || id,
    title,
    description: asNullableString(row.description ?? row.Description),
    objective: asNullableString(row.objective ?? row.Objective),
    jprsMet: asNullableString(row.jprsMet ?? row.JprsMet),
    resourceMaterial: asNullableString(row.resourceMaterial ?? row.ResourceMaterial),
    iconName: asNullableString(row.iconName ?? row.IconName),
    iconBackgroundColor: asNullableString(row.iconBackgroundColor ?? row.IconBackgroundColor),
    iconColor: asNullableString(row.iconColor ?? row.IconColor),
    sizeBytes: asNumber(row.sizeBytes ?? row.SizeBytes),
    lastModified: asNullableString(row.lastModified ?? row.LastModified),
    sasUrl,
    iconUrl: asNullableString(row.iconUrl ?? row.IconUrl),
    featureType: asNullableString(row.featureType ?? row.FeatureType),
    publicationStatus: asNullableString(row.publicationStatus ?? row.PublicationStatus),
    categoryName: asNullableString(row.categoryName ?? row.CategoryName),
    categoryIconName: asNullableString(row.categoryIconName ?? row.CategoryIconName),
    categoryIconUrl: asNullableString(row.categoryIconUrl ?? row.CategoryIconUrl),
    categoryIconColor: asNullableString(row.categoryIconColor ?? row.CategoryIconColor),
    categoryIconBackgroundColor: asNullableString(
      row.categoryIconBackgroundColor ?? row.CategoryIconBackgroundColor,
    ),
    imageUrl: asNullableString(row.imageUrl ?? row.ImageUrl),
    displayDate: asNullableString(row.displayDate ?? row.DisplayDate),
  }
}

export async function fetchTrainingDocuments(): Promise<TrainingDocumentDto[]> {
  const raw = await httpJson<unknown>(BUXUS_GET_TRAINING_DOCUMENTS_URL, {
    method: 'GET',
    auth: true,
  })
  const list = pickList(raw)
  const out: TrainingDocumentDto[] = []
  for (const item of list) {
    const doc = normalizeTrainingDocument(item)
    if (doc) out.push(doc)
  }
  return out
}
